import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { AudioFile, Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { creativesService, type Creative } from '../../services/creatives.service';
import { formatDuration, formatFileSize, SOURCE_LABEL } from './creative-format';

export default function CreativeViewerDialog({ creative, onClose, onDownload }: {
  creative: Creative | null;
  onClose: () => void;
  onDownload: (c: Creative) => void;
}) {
  // The list's signed URLs expire after an hour, so the viewer signs its own on each opening.
  const { data: fresh, isError, isFetched } = useQuery({
    queryKey: ['creative-view', creative?.id],
    queryFn: () => creativesService.getById(creative!.id),
    enabled: !!creative,
    staleTime: 0,
    gcTime: 0,
  });
  const c = creative && (fresh?.id === creative.id ? fresh : creative);
  const src = fresh?.id === creative?.id ? fresh?.signed_url : undefined;
  return (
    <Dialog open={!!c} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="creative-viewer-title">
      {c && (
        <>
          <DialogTitle id="creative-viewer-title" noWrap title={c.name}>{c.name}</DialogTitle>
          <DialogContent>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'grey.900', borderRadius: 1, minHeight: 240, maxHeight: '70vh', overflow: 'hidden',
            }}>
              {!src ? (
                isError || isFetched ? <Typography color="grey.400">Preview unavailable</Typography> : <CircularProgress sx={{ color: 'grey.500' }} />
              ) : c.type === 'image' ? (
                <Box component="img" src={src} alt={c.name} sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              ) : c.type === 'video' ? (
                <Box component="video" src={src} controls autoPlay sx={{ maxWidth: '100%', maxHeight: '70vh' }} />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
                  <AudioFile sx={{ fontSize: 72, color: 'grey.500' }} />
                  <audio src={src} controls autoPlay />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Chip size="small" label={c.type} />
              {c.width && c.height && <Chip size="small" label={`${c.width}×${c.height}`} />}
              {c.duration_seconds != null && <Chip size="small" label={formatDuration(c.duration_seconds)} />}
              <Chip size="small" label={formatFileSize(c.file_size)} />
              <Chip size="small" label={SOURCE_LABEL[c.source] ?? c.source} />
              {c.tags.map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button startIcon={<Download />} onClick={() => onDownload(c)}>Download</Button>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
